import { Box, Breadcrumbs, Link, Typography } from '@mui/material';
import React, { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import useThemeContext from '../../themes/hooks/useThemeContext';

interface PageContainerProps {
     /**
      * Título da página
      */
     title: string;

     /**
      * Descrição da página (opcional)
      */
     description?: string;

     /**
      * Ícone opcional para exibir ao lado do título
      */
     icon?: ReactNode;

     /**
      * Breadcrumbs para navegação
      * Formato: [{ label: 'Home', path: '/' }, { label: 'Eventos', path: '/eventos' }]
      */
     breadcrumbs?: Array<{ label: string; path: string }>;

     /**
      * Botões ou ações a serem exibidos no cabeçalho da página
      */
     actions?: ReactNode;

     /**
      * Conteúdo principal da página
      */
     children: ReactNode;
}

/**
 * Componente de container para padronizar o layout das páginas
 */
const PageContainer: React.FC<PageContainerProps> = ({ title, description, icon, breadcrumbs, actions, children }) => {
     const { theme } = useThemeContext();
     const isDark = theme.palette.mode === 'dark';

     return (
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%' }}>
               <Box
                    sx={{
                         p: 1,
                         mx: 1,
                         my: 1,
                         bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)',
                         backdropFilter: 'blur(8px)',
                         borderRadius: 2,
                         border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                         boxShadow: theme.shadows[1],
                         display: 'flex',
                         flexDirection: 'column',
                         minHeight: 'calc(100vh - 16px)',
                         width: 'calc(100% - 16px)',
                    }}
               >
                    {/* Header da página */}
                    <Box sx={{ mb: 3, flexShrink: 0 }}>
                         {breadcrumbs && (
                              <Breadcrumbs sx={{ mb: 1 }}>
                                   {breadcrumbs.map((item, index) => {
                                        const isLast = index === breadcrumbs.length - 1;

                                        return isLast ? (
                                             <Typography
                                                  key={item.path}
                                                  color='text.primary'
                                             >
                                                  {item.label}
                                             </Typography>
                                        ) : (
                                             <Link
                                                  key={item.path}
                                                  component={RouterLink}
                                                  to={item.path}
                                                  color='inherit'
                                                  underline='hover'
                                             >
                                                  {item.label}
                                             </Link>
                                        );
                                   })}
                              </Breadcrumbs>
                         )}

                         <Box
                              display='flex'
                              flexDirection={{ xs: 'column', sm: 'row' }}
                              justifyContent='space-between'
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                              gap={2}
                         >
                              <Box
                                   paddingInline={2}
                                   display='flex'
                                   alignItems='center'
                                   gap={2}
                              >
                                   {icon && (
                                        <Box
                                             sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  color: 'text.secondary',
                                                  fontSize: '2rem',
                                             }}
                                        >
                                             {icon}
                                        </Box>
                                   )}
                                   <Box>
                                        <Typography
                                             variant='h5'
                                             component='h1'
                                             fontWeight='bold'
                                        >
                                             {title}
                                        </Typography>

                                        {description && (
                                             <Typography
                                                  variant='body2'
                                                  color='text.secondary'
                                                  sx={{ mt: 0.5 }}
                                             >
                                                  {description}
                                             </Typography>
                                        )}
                                   </Box>
                              </Box>
                              {actions && (
                                   <Box
                                        sx={{
                                             display: 'flex',
                                             flexWrap: 'wrap',
                                             gap: 1,
                                             justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                                             width: { xs: '100%', sm: 'auto' },
                                        }}
                                   >
                                        {actions}
                                   </Box>
                              )}
                         </Box>
                    </Box>
                    {/* Conteúdo da página */}
                    <Box
                         sx={{
                              flexGrow: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              width: '100%',
                         }}
                    >
                         {children}
                    </Box>
               </Box>
          </Box>
     );
};

export default PageContainer;
